import { useRef, type ReactNode } from 'react'
import { useModalShell } from './useModalShell'

/**
 * One overlay contract, for every dialog in the app (D-515).
 *
 * <b>Twelve full-screen overlays had five different ways to get out.</b> Some
 * closed on Escape, some on a backdrop tap, some on neither; five carried no
 * `role`, no `aria-modal` and no accessible name; and <em>none of them</em>
 * trapped focus, so Tab from any dialog walked into the page behind it. On the
 * parent portal, two buttons on the same row opened dialogs that behaved
 * differently — which is the finding the owner brought from another app, in
 * this app's own words.
 *
 * <b>This is the shell, not the dialog.</b> Each caller keeps its own body and
 * its own buttons; what moves here is the part that should never have been
 * written twice — the overlay, the way out, and the promises a modal makes to
 * a keyboard.
 *
 * <b>The behaviour lives in {@link useModalShell}</b>, so the five large
 * dialogs that cannot reasonably be restructured — their panel is a `<form>`,
 * or they carry their own scrolling and a nested dialog — can have the same
 * contract without moving a tag. One implementation, two ways to reach it.
 *
 * <b>The backdrop needs the press remembered, not just the click.</b> A click
 * is dispatched at the nearest common ancestor of its press and release, so
 * selecting text inside a form and overshooting onto the dim area retargets the
 * click to the overlay. Closing on that discards whatever the person was
 * typing. `observed` in Edge, on `ReportAbsence`, against a first attempt that
 * used the click target alone.
 */
export default function Modal({
  onDismiss,
  label,
  labelledBy,
  describedBy,
  role = 'dialog',
  panelClassName,
  overlayClassName = 'fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-5',
  children,
}: {
  /**
   * Called for Escape and for a backdrop tap.
   *
   * Omit it for a dialog that must be answered — nothing dismisses it, and the
   * body has to offer its own way out. That is a deliberate choice a caller
   * makes, rather than something four dialogs ended up with by accident.
   */
  onDismiss?: () => void
  /** The dialog's accessible name, when it has no visible heading to point at. */
  label?: string
  /** The id of its visible heading, which is better when there is one. */
  labelledBy?: string
  describedBy?: string
  role?: 'dialog' | 'alertdialog'
  panelClassName: string
  overlayClassName?: string
  children: ReactNode
}) {
  const panel = useRef<HTMLDivElement>(null)
  const pressedBackdrop = useRef(false)

  useModalShell({ panel, onDismiss })

  return (
    <div
      data-modal-overlay=""
      className={overlayClassName}
      onMouseDown={(e) => {
        pressedBackdrop.current = e.target === e.currentTarget
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && pressedBackdrop.current) onDismiss?.()
      }}
    >
      <div
        ref={panel}
        tabIndex={-1}
        role={role}
        aria-modal="true"
        aria-label={labelledBy ? undefined : label}
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        className={panelClassName}
      >
        {children}
      </div>
    </div>
  )
}
